import { ehQrDeOutroSistema, lerQr, type QrLido } from './qr';
import type { NovoRetorno } from './retorno';

/**
 * DO QUE O ADESIVO DISSE À OPÇÃO DE UM CAMPO DE ESCOLHA (pedido do dono,
 * 31/10/2026): *"nos módulos que tenham a seleção de equipamento, por exemplo
 * o lançamento de combustível, um leitor que leia o QR e identifique o
 * equipamento automaticamente, assim zeramos a chance de erros de
 * preenchimento"*.
 *
 * Uma função só, pura, para os três caminhos de leitura e para as duas pontas:
 * a câmera do celular, a câmera do computador (e a foto) e o leitor de código
 * USB ou Bluetooth, que DIGITA o conteúdo do QR no campo e aperta Enter.
 *
 * ## Contra a lista DAQUELE campo, e não contra a frota
 *
 * O campo da mesa e o do celular raramente mostram a frota inteira: o
 * lançamento só mostra a máquina apta ao tanque, a folha de turno só a de
 * horímetro, o filtro só a que tem vencimento. Escolher pelo adesivo uma
 * máquina que a lista não tem gravaria o que a tela existe para impedir, ou
 * deixaria o campo com um valor que ele não sabe desenhar. Então a leitura
 * procura nas OPÇÕES do campo e, quando não acha, na frota (o `universo`),
 * só para dizer a coisa certa: "BAS-004 existe, mas não está entre as
 * máquinas desta lista", e não "não existe".
 *
 * ## O tanque entra sem reescrever (pedido do dono, para depois)
 *
 * O que a leitura sabe da máquina mora em `TipoDeAdesivo`: o tipo do QR
 * antigo que aponta direto o `id` (`EQ`), e as palavras do recado. O tanque
 * entra com o tipo dele em `qr.ts` (`TQ`) e uma entrada aqui, e os campos de
 * tanque passam `tipo` — o resolvedor, o leitor e o teclado não mudam. O
 * código nu (`TQ-01`) é procurado nas opções do campo que o pediu, e por isso
 * o de tanque nunca vira máquina: no campo de máquina ele não está na lista.
 */

/** O que um adesivo identifica: o `id` (adesivo antigo) e o código impresso (o de hoje). */
export interface AlvoDoAdesivo {
  readonly id: string;
  readonly codigo: string;
}

/** A opção de um campo de escolha que um adesivo pode preencher (`opcaoDaMaquina` a monta). */
export interface OpcaoComAdesivo {
  readonly valor: string;
  readonly adesivo?: AlvoDoAdesivo;
}

/** O tipo de QR antigo que traz o `id` (`CTF1:EQ:<id>`). */
type TipoDoQrComId = Extract<QrLido, { readonly id: string }>['tipo'];

/** O que muda de uma coisa adesivada para outra: o tipo do QR antigo e as palavras. */
export interface TipoDeAdesivo {
  readonly qr: TipoDoQrComId;
  /** "máquina", "tanque". */
  readonly singular: string;
  /** "máquinas", "tanques". */
  readonly plural: string;
  /** O artigo: "a" máquina, "o" tanque. */
  readonly artigo: 'a' | 'o';
  /** "Nenhuma máquina", "Nenhum tanque". */
  readonly nenhum: string;
  /** Por que a coisa que existe pode não estar na lista, quando a tela não diz. */
  readonly foraDaLista: string;
}

export const ADESIVO_DE_MAQUINA: TipoDeAdesivo = {
  qr: 'equipamento',
  singular: 'máquina',
  plural: 'máquinas',
  artigo: 'a',
  nenhum: 'Nenhuma máquina',
  foraDaLista: 'esta tela mostra só as que valem aqui, pela obra, pela situação ou pelo seu acesso',
};

export type LeituraDoAdesivo<O> =
  | { readonly tipo: 'escolhida'; readonly opcao: O }
  /** A leitura é deste sistema (ou é um QR de outro) e não preenche: a frase diz o que fazer. */
  | { readonly tipo: 'recado'; readonly recado: string; readonly codigo?: string }
  /** Nada que dizer: texto vazio. */
  | { readonly tipo: 'nada' };

function mesmoCodigo(a: string, b: string): boolean {
  return a.trim().toUpperCase() === b.trim().toUpperCase();
}

function achar<T>(itens: readonly T[], alvo: (item: T) => AlvoDoAdesivo | undefined, lido: QrLido): T | undefined {
  return itens.find((item) => {
    const a = alvo(item);
    if (a === undefined) return false;
    return lido.tipo === 'codigo' ? mesmoCodigo(a.codigo, lido.codigo) : a.id === lido.id;
  });
}

/**
 * Resolve o texto lido (câmera, foto ou teclado) contra as opções do campo.
 *
 * - `escolhida`: a opção do campo, pelo código (sem diferença de caixa) ou
 *   pelo `id` do adesivo antigo;
 * - `recado`: o QR é de outro sistema, o adesivo é de outro tipo, a coisa
 *   existe mas não está nesta lista, ou não existe — cada um com a frase do
 *   que fazer;
 * - `nada`: não havia o que ler.
 *
 * `universo` indefinido é a frota que esta pessoa não lê, ou que ainda não
 * chegou: aí a leitura não afirma que a coisa não existe.
 */
export function resolverAdesivo<O extends OpcaoComAdesivo>(
  texto: string,
  contexto: {
    readonly opcoes: readonly O[];
    readonly universo?: readonly AlvoDoAdesivo[] | undefined;
    /** Por que a coisa lida pode não estar NESTA lista, quando a tela sabe dizer. */
    readonly porQueFora?: string | undefined;
    readonly tipo?: TipoDeAdesivo;
  },
): LeituraDoAdesivo<O> {
  const tipo = contexto.tipo ?? ADESIVO_DE_MAQUINA;
  const limpo = texto.trim();
  if (limpo === '') return { tipo: 'nada' };

  const lido = lerQr(limpo);
  if (lido === null) {
    return ehQrDeOutroSistema(limpo)
      ? { tipo: 'recado', recado: `Este código não é de ${tipo.artigo === 'a' ? 'uma' : 'um'} ${tipo.singular}. Aponte para o adesivo colado nel${tipo.artigo}.` }
      : { tipo: 'nada' };
  }
  if (lido.tipo !== 'codigo' && lido.tipo !== tipo.qr) {
    return { tipo: 'recado', recado: `Este adesivo não é de ${tipo.artigo === 'a' ? 'uma' : 'um'} ${tipo.singular}. Escolha pela lista.` };
  }

  const opcao = achar(contexto.opcoes, (o) => o.adesivo, lido);
  if (opcao !== undefined) return { tipo: 'escolhida', opcao };

  const outra = contexto.universo === undefined ? undefined : achar(contexto.universo, (a) => a, lido);
  if (outra !== undefined) {
    const porQue = contexto.porQueFora ?? tipo.foraDaLista;
    return {
      tipo: 'recado',
      codigo: outra.codigo,
      recado: `${outra.codigo} existe, mas não está entre ${tipo.artigo}s ${tipo.plural} desta lista: ${porQue}. Escolha outr${tipo.artigo} pela lista.`,
    };
  }

  if (lido.tipo === 'codigo') {
    return contexto.universo === undefined
      ? {
          tipo: 'recado',
          codigo: lido.codigo,
          recado: `${tipo.nenhum} desta lista tem o código ${lido.codigo}. Confira o código impresso no adesivo, ou escolha pela lista.`,
        }
      : {
          /* A frase que o leitor do celular já dizia (decisão 64): o adesivo de
             máquina ainda sem cadastro é normal, e não defeito. */
          tipo: 'recado',
          codigo: lido.codigo,
          recado: `${tipo.nenhum} tem o código ${lido.codigo}. Cadastre ${tipo.artigo} ${tipo.singular} antes de lançar, ou escolha pela lista.`,
        };
  }
  return {
    tipo: 'recado',
    recado:
      contexto.universo === undefined
        ? `${tipo.nenhum} desta lista tem este adesivo. Escolha pela lista.`
        : `${tipo.nenhum} da frota tem este adesivo: ${tipo.artigo} ${tipo.singular} pode ter saído do cadastro. Escolha pela lista e peça um adesivo novo.`,
  };
}

/**
 * O LEITOR QUE DIGITA (USB ou Bluetooth, no modo teclado): ele escreve o
 * conteúdo do QR no campo, em rajada, e aperta Enter. Esta função decide se
 * aquele Enter é dele, e só então a lista deixa de fazer o que o Enter faz.
 *
 * A regra protege quem DIGITA na busca: "BAS" + Enter continua escolhendo a
 * opção marcada, como sempre. O Enter é da leitura quando o texto é o
 * conteúdo de um adesivo E:
 * - é o formato antigo (`CTF1:EQ:<id>`), que pessoa nenhuma digita; ou
 * - é o código de uma opção da lista, exato (sem diferença de caixa); ou
 * - chegou em rajada (`rapida`), que é como o leitor digita, e aí o código
 *   que a lista não tem ganha o recado em vez de escolher outra coisa.
 */
export function enterDoLeitor(texto: string, rapida: boolean, opcoes: readonly OpcaoComAdesivo[]): boolean {
  const lido = lerQr(texto);
  if (lido === null) return false;
  if (lido.tipo !== 'codigo') return true;
  if (rapida) return true;
  return opcoes.some((o) => o.adesivo !== undefined && mesmoCodigo(o.adesivo.codigo, lido.codigo));
}

/** O maior intervalo entre duas teclas de um leitor que digita. Gente digitando passa disto. */
export const INTERVALO_DO_LEITOR_MS = 80;

/**
 * As teclas chegaram em rajada? Leitor de código digita com 5 a 40 ms entre
 * as teclas; gente, mesmo rápida, passa de 100 ms em algum par. Três teclas
 * no mínimo: duas teclas juntas acontecem com qualquer um.
 */
export function chegouEmRajada(instantes: readonly number[]): boolean {
  if (instantes.length < 3) return false;
  for (let i = 1; i < instantes.length; i += 1) {
    const atual = instantes[i] ?? 0;
    const anterior = instantes[i - 1] ?? 0;
    if (atual - anterior > INTERVALO_DO_LEITOR_MS) return false;
  }
  return true;
}

/** O que a opção mostra e o retorno repete: o código, a segunda linha e as etiquetas. */
export interface OpcaoParaORetorno {
  readonly rotulo: string;
  readonly detalhe?: string;
  readonly etiquetas?: readonly { readonly texto: string; readonly titulo?: string }[];
}

/**
 * O toast da leitura que preencheu (R04, p.32): o que foi feito, em quê (o
 * código e a segunda linha) e os valores que a pessoa confere de olho — o
 * código interno e a placa, as mesmas etiquetas do campo.
 */
export function retornoDaLeitura(opcao: OpcaoParaORetorno, tipo: TipoDeAdesivo = ADESIVO_DE_MAQUINA): NovoRetorno {
  const fatos = (opcao.etiquetas ?? []).map((e) => (e.titulo ? `${e.titulo} ${e.texto}` : e.texto));
  const Singular = tipo.singular.charAt(0).toUpperCase() + tipo.singular.slice(1);
  return {
    tom: 'sucesso',
    titulo: `${Singular} lid${tipo.artigo} no adesivo`,
    alvo: { codigo: opcao.rotulo, ...(opcao.detalhe ? { nome: opcao.detalhe } : {}) },
    ...(fatos.length > 0 ? { fatos } : {}),
  };
}

/** O toast da leitura que NÃO preencheu: o campo ficou como estava, e a frase diz o que fazer. */
export function retornoDoRecado(recado: string, codigo: string | undefined, tipo: TipoDeAdesivo = ADESIVO_DE_MAQUINA): NovoRetorno {
  const Singular = tipo.singular.charAt(0).toUpperCase() + tipo.singular.slice(1);
  return {
    tom: 'falha',
    titulo: `${Singular} não preenchid${tipo.artigo} pelo adesivo`,
    ...(codigo ? { alvo: { codigo } } : {}),
    comoResolver: recado,
  };
}
